import { useState } from "react"

import axios from "axios"
import { useTranslation } from "react-i18next"

import type { HostedAgentSelftestResult } from "@/api/admin-api"

import { CheckCircle2, FlaskConical, XCircle } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"

import { useSelftestHostedAgentMutation } from "../hooks/admin-queries"

const MIN_TIMEOUT_SECONDS = 1
const MAX_TIMEOUT_SECONDS = 300

// Admin test-chat panel (HERMES Fas 5): runs the golden-turn selftest via
// POST /api/admin/hosted-agents/<id>/selftest. This is a REAL durable turn
// through the agent's connected Hermes container (never a bypass) — the run
// is flagged `initiator: selftest` and does not count against user budgets.

export function HostedAgentSelftestPanel({ agentId }: { agentId: string }) {
  const { t } = useTranslation()
  const selftestMutation = useSelftestHostedAgentMutation()
  const [timeoutSeconds, setTimeoutSeconds] = useState("60")
  const [result, setResult] = useState<HostedAgentSelftestResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [timeoutError, setTimeoutError] = useState(false)

  const isRunning = selftestMutation.isPending

  const handleRun = async () => {
    const parsed = Number(timeoutSeconds.trim())
    const validTimeout =
      Number.isInteger(parsed) && parsed >= MIN_TIMEOUT_SECONDS && parsed <= MAX_TIMEOUT_SECONDS
    setTimeoutError(!validTimeout)
    if (!validTimeout) return

    setResult(null)
    setErrorMessage(null)
    try {
      const outcome = await selftestMutation.mutateAsync({ agentId, timeoutSeconds: parsed })
      setResult(outcome)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        setErrorMessage(
          t("adminDashboard.hostedAgents.selftest.unavailable", {
            defaultValue:
              "The run service is not connected — deploy and connect the agent's container first.",
          })
        )
      } else if (axios.isAxiosError(error) && error.response?.status === 409) {
        // Server-side single-flight guard: one selftest per agent at a time.
        setErrorMessage(
          t("adminDashboard.hostedAgents.selftest.inFlight", {
            defaultValue: "A self-test is already running for this agent — wait for it to finish.",
          })
        )
      } else {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : t("adminDashboard.hostedAgents.selftest.error", {
                defaultValue: "The self-test could not be started.",
              })
        )
      }
    }
  }

  return (
    <div className="space-y-4">
      <Alert>
        <FlaskConical className="size-4" />
        <AlertTitle>
          {t("adminDashboard.hostedAgents.selftest.title", { defaultValue: "Test chat" })}
        </AlertTitle>
        <AlertDescription>
          {t("adminDashboard.hostedAgents.selftest.help", {
            defaultValue:
              "Runs one real end-to-end turn through the agent (prompt → model → tool call → answer) and reports the verdict. It requires the agent's container to be deployed and connected. The run is audit-flagged as a self-test and never counts against user budgets.",
          })}
        </AlertDescription>
      </Alert>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-2">
          <Label htmlFor="hosted-agent-selftest-timeout">
            {t("adminDashboard.hostedAgents.selftest.timeoutLabel", {
              defaultValue: "Timeout (seconds)",
            })}
          </Label>
          <Input
            className="w-32"
            disabled={isRunning}
            id="hosted-agent-selftest-timeout"
            inputMode="numeric"
            max={MAX_TIMEOUT_SECONDS}
            min={MIN_TIMEOUT_SECONDS}
            type="number"
            value={timeoutSeconds}
            onChange={(event) => setTimeoutSeconds(event.target.value)}
          />
        </div>
        <Button disabled={isRunning} type="button" onClick={() => void handleRun()}>
          {isRunning ? (
            <>
              <Spinner />
              {t("adminDashboard.hostedAgents.selftest.running", { defaultValue: "Running…" })}
            </>
          ) : (
            t("adminDashboard.hostedAgents.selftest.run", { defaultValue: "Run self-test" })
          )}
        </Button>
      </div>
      {timeoutError ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
          {t("adminDashboard.hostedAgents.selftest.timeoutInvalid", {
            defaultValue: "Enter a whole number between {{min}} and {{max}}.",
            min: MIN_TIMEOUT_SECONDS,
            max: MAX_TIMEOUT_SECONDS,
          })}
        </p>
      ) : null}

      {isRunning ? (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Spinner className="size-4" />
          {t("adminDashboard.hostedAgents.selftest.inProgress", {
            defaultValue: "Self-test running — executing one durable turn…",
          })}
        </p>
      ) : null}

      {result?.passed === true ? (
        <Alert>
          <CheckCircle2 className="text-foreground size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.selftest.passed", {
              defaultValue: "Self-test passed",
            })}
          </AlertTitle>
          <AlertDescription>
            <SelftestDetails result={result} />
          </AlertDescription>
        </Alert>
      ) : null}
      {result && !result.passed ? (
        <Alert
          className="border-foreground bg-destructive text-destructive-foreground dark:border-foreground [&>svg]:text-destructive-foreground"
          variant="destructive"
        >
          <XCircle className="size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.selftest.failed", {
              defaultValue: "Self-test failed",
            })}
          </AlertTitle>
          <AlertDescription>
            <SelftestDetails result={result} />
          </AlertDescription>
        </Alert>
      ) : null}

      {errorMessage ? (
        <Alert
          className="border-foreground bg-destructive text-destructive-foreground dark:border-foreground [&>svg]:text-destructive-foreground"
          variant="destructive"
        >
          <XCircle className="size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.selftest.error", {
              defaultValue: "The self-test could not be started.",
            })}
          </AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}

function SelftestDetails({ result }: { result: HostedAgentSelftestResult }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-1">
      {result.detail ? <p>{result.detail}</p> : null}
      <p className="text-xs">
        {result.duration_ms !== null
          ? t("adminDashboard.hostedAgents.selftest.duration", {
              defaultValue: "Duration: {{seconds}} s",
              seconds: (result.duration_ms / 1000).toFixed(1),
            })
          : null}
        {result.duration_ms !== null && result.run_id ? " · " : null}
        {result.run_id
          ? t("adminDashboard.hostedAgents.selftest.runId", {
              defaultValue: "Run: {{runId}}",
              runId: result.run_id,
            })
          : null}
      </p>
    </div>
  )
}
